// Contrato con el servidor OAuth del MCP de alumnos integrado en apis-node
// (`POST /oauth/authorize/decision`, docs/mcp-integrado.md §2 de apis-node).
// Usa `fetch` a propósito, NO HttpService ni el axios de auto-matrícula: no
// queremos interceptores, ni el toast global de errores, ni el parámetro
// `userEmail` (el cuerpo es `.strict()` en apis-node: un campo extra da 400).
// Nada de este módulo debe registrar uid, email, JWT ni respuestas.

// Mapa fijo (no configurable): el origen de la API no puede venir de env vars
// manipulables. `REACT_APP_NODE_API` solo se usa para comprobar que coincide.
// Debe ser el mismo origen que `MCP_PUBLIC_BASE_URL` de apis-node en cada entorno.
export const API_ORIGIN_POR_DESPLIEGUE = {
  local: 'http://localhost:4000',
  test: 'https://apis.testn.cedetecgroup.com', // [A CONFIRMAR]
  production: 'https://apis.n.cedetecgroup.com',
};

export const RUTA_DECISION = '/oauth/authorize/decision';

// request_id de apis-node: 32 bytes aleatorios en base64url sin relleno.
export const UID_REGEX = /^[A-Za-z0-9_-]{43}$/;

const RUTA_REANUDAR_REGEX = /^\/oauth\/authorize\/resume\/[A-Za-z0-9_-]{43}$/;

// Espejo del valor por defecto de MCP_MAX_AUTH_AGE_S en apis-node. Solo se usa
// para pedir el login antes de que el servidor responda 401
// `reautenticacion_requerida`; la decisión real la toma siempre apis-node.
export const EDAD_MAX_LOGIN_S = 600;

// Tiempo que la página debe llevar visible y con foco antes de habilitar
// Continuar/Volver: un doble clic preparado desde una ventana emergente no debe
// caer sobre esos botones.
export const ESPERA_ACTIVACION_MS = 500;

export const uidValido = (uid) => typeof uid === 'string' && UID_REGEX.test(uid);

// Devuelve el origen de la API o null (falla cerrado) si el despliegue no está
// en el mapa o si REACT_APP_NODE_API apunta a otro origen.
export const resolverApiOrigin = ({ REACT_APP_DEPLOYMENT, REACT_APP_NODE_API } = {}) => {
  if (!Object.prototype.hasOwnProperty.call(API_ORIGIN_POR_DESPLIEGUE, REACT_APP_DEPLOYMENT)) {
    return null;
  }
  const esperado = API_ORIGIN_POR_DESPLIEGUE[REACT_APP_DEPLOYMENT];
  try {
    return new URL(REACT_APP_NODE_API).origin === esperado ? esperado : null;
  } catch {
    return null;
  }
};

// `redirectTo` solo es navegable si es exactamente
// {apiOrigin}/oauth/authorize/resume/{43 caracteres base64url}, sin
// credenciales, query ni fragmento (ni siquiera `?` o `#` vacíos: la cadena debe
// ser ya la forma canónica `origin + pathname`). El código OAuth nunca pasa por
// esta SPA.
export const redirectToValido = (redirectTo, apiOrigin) => {
  if (typeof redirectTo !== 'string' || !apiOrigin) return false;
  let u;
  try {
    u = new URL(redirectTo);
  } catch {
    return false;
  }
  return (
    redirectTo === `${u.origin}${u.pathname}` &&
    u.origin === apiOrigin &&
    RUTA_REANUDAR_REGEX.test(u.pathname) &&
    u.username === '' &&
    u.password === '' &&
    u.search === '' &&
    u.hash === ''
  );
};

export const normalizarEmail = (email) =>
  typeof email === 'string' ? email.trim().toLowerCase() : '';

const decodificarBase64Url = (segmento) => {
  const base64 = segmento.replace(/-/g, '+').replace(/_/g, '/');
  const binario = atob(base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '='));
  // Bytes → UTF-8 (los nombres del perfil pueden llevar tildes).
  return decodeURIComponent(
    Array.from(binario, (c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`).join(''),
  );
};

// Lee (SIN verificar: eso lo hace apis-node) los claims del JWT de sesión del
// panel que se usan en la pantalla: el email que firmó el servidor al hacer
// login (`google_profileObj.email`, el mismo que usará `decision`), `auth_time`
// y `exp`. Devuelve null si el token no tiene la forma esperada.
export const leerClaimsSesion = (token) => {
  if (typeof token !== 'string') return null;
  const partes = token.split('.');
  if (partes.length !== 3 || !/^[A-Za-z0-9_-]+$/.test(partes[1])) return null;
  let payload;
  try {
    payload = JSON.parse(decodificarBase64Url(partes[1]));
  } catch {
    return null;
  }
  const email = normalizarEmail(payload?.google_profileObj?.email);
  if (!email.includes('@')) return null;
  return {
    email,
    authTime: Number.isInteger(payload.auth_time) ? payload.auth_time : null,
    exp: Number.isInteger(payload.exp) ? payload.exp : null,
  };
};

// Devuelve { status, data }. Un fallo de red lanza (lo trata el llamador).
const llamarDecision = async (apiOrigin, { uid, decision, token }) => {
  const headers = { Accept: 'application/json', 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${apiOrigin}${RUTA_DECISION}`, {
    method: 'POST',
    headers,
    // Cuerpo exacto del contrato: ni userEmail, ni profileObj, ni query.
    body: JSON.stringify({ request_id: uid, decision }),
    credentials: 'include',
    cache: 'no-store',
    redirect: 'error',
    referrerPolicy: 'no-referrer',
  });

  let data = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }
  return { status: res.status, data };
};

// Aprobar exige el JWT de sesión del panel: la identidad sale SOLO de él.
export const aprobar = (apiOrigin, uid, token) =>
  llamarDecision(apiOrigin, { uid, decision: 'aprobar', token });

// Cancelar no necesita sesión (apis-node ignora Authorization): no se envía.
export const cancelar = (apiOrigin, uid) =>
  llamarDecision(apiOrigin, { uid, decision: 'cancelar' });
